export const SHIPPING_ESTIMATE_CATEGORIES = [
  { code: "general-goods", label: "General goods" },
  { code: "clothing-apparel", label: "Clothing & apparel" },
  { code: "shoes-bags", label: "Shoes & bags" },
  { code: "electronics", label: "Electronics" },
  { code: "cosmetics", label: "Cosmetics" },
  { code: "liquids-perfumes", label: "Liquids & perfumes" },
  { code: "with-batteries", label: "With batteries" },
  { code: "sensitive-item", label: "Sensitive item" },
] as const;

export type ShippingEstimateCategoryCode = (typeof SHIPPING_ESTIMATE_CATEGORIES)[number]["code"];
export type ShippingEstimateFieldState = "unknown" | "not-applicable";
export type ShippingEstimateRouteState = "loading" | "no-recommendation" | "unknown" | "error" | "ready";
export type ShippingEstimateCollectionState = "loading" | "empty" | "unknown" | "error" | "ready";
export type ShippingEstimateDetailState = "loading" | "loaded" | "partial" | "error";
export type ShippingEstimateMoneySource = "quote" | "long-term-discount" | "coupon" | "tax" | "insurance" | "surcharge";

export type ShippingEstimateError = {
  code: string;
  displayMessage?: string;
};

export type ShippingEstimateMoney = {
  amountMinor: number;
  currency: string;
  precision: number;
  displayAmount: string;
  originalAmount?: string;
  source: ShippingEstimateMoneySource;
};

export type ShippingEstimateFact = {
  value?: string;
  state?: ShippingEstimateFieldState;
  help?: string;
};

export type ShippingEstimateLabeledFact = ShippingEstimateFact & { label: string };

export type ShippingEstimateRouteTag = {
  kind: "long-term-discount" | "coupon-discount" | "tax-responsibility" | "insurance" | "neutral";
  label: string;
};

export type ShippingEstimateUnavailableReason = {
  code: "destination" | "province" | "category" | "weight" | "dimensions" | "restriction" | "capacity" | "quote-expired" | "unknown";
  displayMessage: string;
};

export type ShippingEstimateInternalAction = { enabled: boolean; href?: string };
export type ShippingEstimateCategoryGroup = { items?: string[]; total?: number; state?: ShippingEstimateFieldState };

export type ShippingEstimateRouteDetail = {
  state: ShippingEstimateDetailState;
  errorMessage?: string;
  costBreakdown?: { total?: ShippingEstimateMoney; rows?: ShippingEstimateLabeledFact[] };
  weightCalculationRules?: ShippingEstimateLabeledFact[];
  compatibility?: {
    lowerWeightLimit?: ShippingEstimateLabeledFact;
    upperWeightLimit?: ShippingEstimateLabeledFact;
    sizeLimit?: ShippingEstimateLabeledFact;
    shippable?: ShippingEstimateCategoryGroup;
    nonShippable?: ShippingEstimateCategoryGroup;
  };
  protectionPolicy?: ShippingEstimateLabeledFact[];
  lineFeatures?: ShippingEstimateLabeledFact[];
};

export type ShippingEstimateRouteQuote = {
  id: string;
  state: ShippingEstimateRouteState;
  recommended?: boolean;
  availableRouteCount?: number;
  name?: string;
  carrier?: string;
  logoUrl?: string;
  tags?: ShippingEstimateRouteTag[];
  pricing?: ShippingEstimateMoney;
  transit?: ShippingEstimateFact;
  chargeableWeight?: ShippingEstimateFact;
  billingMode?: ShippingEstimateFact;
  volumeFormulaHint?: ShippingEstimateFact;
  unavailableReason?: ShippingEstimateUnavailableReason;
  detail?: ShippingEstimateRouteDetail;
  actions?: {
    submitParcel?: ShippingEstimateInternalAction;
    startShopping?: ShippingEstimateInternalAction;
  };
};

export type ShippingEstimateRouteCollection = {
  state: ShippingEstimateCollectionState;
  total?: number;
  promotion?: { title: string; href?: string } | null;
  routes: ShippingEstimateRouteQuote[];
  error?: ShippingEstimateError;
};

export type ShippingEstimateOption = { code: string; label: string };

export type ShippingEstimateViewModel = {
  state: "idle" | "loading" | "ready" | "empty" | "partial" | "error";
  quote?: {
    id: string;
    version: string;
    issuedAt: string;
    expiresAt: string;
    displayCurrency: string;
    sourceCurrencyOnly: boolean;
  };
  selectors?: {
    destinations: ShippingEstimateOption[];
    provinces: ShippingEstimateOption[];
    categories: ShippingEstimateOption[];
  };
  announcement?: { title: string; copy: string; href?: string } | null;
  recommended?: ShippingEstimateRouteQuote | null;
  available: ShippingEstimateRouteCollection;
  unavailable: ShippingEstimateRouteCollection;
  couponHref?: string | null;
  error?: ShippingEstimateError;
};

export type ShippingEstimateApiResponse =
  | { ok: true; estimate: ShippingEstimateViewModel }
  | { ok: false; error: ShippingEstimateError; estimate?: ShippingEstimateViewModel };
